import { ArrowRightAltRounded, CreditCard, FeedbackRounded, MeetingRoomRounded, People } from '@material-ui/icons';
import { Huddle, HuddleStatus, ProposalStatus, Role } from 'lib/generated/graphql';
import moment from 'moment';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useAuth from 'src/shared-components/Auth';

const DEFAULT_USER_AVATAR = process.env.DEFAULT_USER_AVATAR || '';

// Assets
const colorbg = {
  gray: 'bg-[#F2F2F2]',
  green: 'bg-[#E9FAF0]',
  yellow: 'bg-[#FFF8E7]',
};

const TeamAvatars = ({ huddle }: { huddle: Huddle }) => {
  return (
    <div className="flex bg-white rounded-lg border font-small flex items-center whitespace-nowrap px-3 py-3">
      {huddle?.proposal?.selectedOptIns?.map(builder => {
        return (
          <div
            key={builder?.user?.id}
            className={`z-30 -ml-1 h-6 w-6 relative rounded-lg outline outline-2 outline-white`}
          >
            <Image className="rounded-lg" layout="fill" src={builder?.user?.photoUrl || DEFAULT_USER_AVATAR} />
          </div>
        );
      })}
      <div className="ml-2 font-xs text-huddleFontGray ">The team</div>
    </div>
  );
};

const JoinRoomButton = ({ huddle }: { huddle: Huddle }) => {
  return (
    <button
      className="bg-white rounded-lg border text-huddleFontGray font-small flex items-center whitespace-nowrap px-3 py-2.5"
      onClick={() => window.open(huddle?.request?.roomLink)}
    >
      <MeetingRoomRounded />
      <span className="font-xs text-huddleFontGray ">Your Huddle Room</span>
      <span className="ml-1 text-huddleFontGray ">
        <ArrowRightAltRounded />
      </span>
    </button>
  );
};

const FeedbackButton = ({ huddle }: { huddle: Huddle }) => {
  const { user } = useAuth();
  return (
    <button
      className="bg-[#3C3D3E] rounded-lg py-2.5 px-4 border border-transparent text-white flex items-center"
      onClick={() =>
        window.open(
          `https://wehuddleup.typeform.com/to/ivvOVCvD#email=${user?.email}&huddle_id=${huddle.id}&name=${
            user.name
          }&huddle_name=${huddle.request.company.name}&teammates=${huddle.builders.length > 1}&customer=${
            user.role === Role.Customer
          }`,
          '_blank',
        )
      }
    >
      <FeedbackRounded />
      <span className="ml-3 font-medium ">Give Feedback!</span>
      <span className="ml-1">
        <ArrowRightAltRounded />
      </span>
    </button>
  );
};

const ViewHuddleAgreementButton = ({ huddle }: { huddle: Huddle }) => {
  const href =
    huddle?.proposal?.status === ProposalStatus.Draft
      ? `/admin/project/${huddle?.request?.slug}/proposal/${huddle?.proposal?.id}/edit`
      : `/huddles/${huddle?.request?.slug}/proposal/${huddle?.proposal?.id}`;
  return (
    <Link href={href} passHref>
      <a>
        <button className="bg-white border border-[#3C3D3E] rounded-lg px-11 py-2.5 ">
          <div className=" text-[#3C3D3E] font-medium">View Huddle Agreement</div>
        </button>
      </a>
    </Link>
  );
};

const LeadReviewAgreementButton = ({ huddle }: { huddle: Huddle }) => {
  const href =
    huddle?.proposal?.status === ProposalStatus.Draft
      ? `/admin/project/${huddle?.request?.slug}/proposal/${huddle?.proposal?.id}/edit`
      : `/huddles/${huddle?.request?.slug}/proposal/${huddle?.proposal?.id}`;
  return (
    <Link href={href} passHref>
      <a>
        <button className="bg-[#323232]  border border-[#3C3D3E] rounded-lg px-11 py-2.5 ">
          <div className="flex flex-row gap-x-3 text-white">
            <People />
            <span className="font-xs text-white ">Review Agreement</span>
          </div>
        </button>
      </a>
    </Link>
  );
};

export const ReviewHuddleAgreementButton = ({ huddle }: { huddle: Huddle }) => {
  return (
    <Link href={`/huddles/${huddle?.request?.slug}/proposal/${huddle?.proposal?.id}`} passHref>
      <a>
        <button className="bg-huddleBlack rounded-lg border font-xs flex items-center whitespace-nowrap px-3 py-3">
          <div className="text-white font-xs"> 📄 Review the Agreement</div>
        </button>
      </a>
    </Link>
  );
};

export const PayNowButton = ({ huddle }: { huddle: Huddle }) => {
  const router = useRouter();
  const { slug } = router.query;

  const navigateToPay = (selectedDate: string) => {
    router.push(`/huddles/${huddle?.request?.slug}/proposal/${huddle.proposal.id}/pay?selectedDate=${selectedDate}`);
  };

  return (
    <button
      className="bg-huddleBlack text-white rounded-lg border font-small flex items-center whitespace-nowrap px-3 py-3 gap-2"
      onClick={navigateToPay ? () => navigateToPay(moment(huddle.reservedStartDate).toISOString()) : null}
    >
      <CreditCard />
      <span className="font-xs ">Pay Now</span>
    </button>
  );
};

const TagCustom = ({ text, hexColor }: { text: string; hexColor: string }) => {
  return (
    <div className={`text-[#868686] whitespace-nowrap text-[14px] py-1.5 px-6 ${hexColor} rounded-[40px]`}>{text}</div>
  );
};

const TeamList = ({ huddle }: { huddle: Huddle }) => {
  return (
    <div>
      {huddle?.proposal?.selectedOptIns?.map(builder => (
        <div key={builder?.user?.name}>
          <div className="flex items-center">
            <div className={`h-8 w-8 rounded-xl relative`}>
              <Image className="rounded-xl" layout="fill" src={builder?.user?.photoUrl || DEFAULT_USER_AVATAR} />
            </div>
            <div className="font-medium ml-4 mr-3 text-[17px]">{builder?.user?.name}</div>
          </div>
          <div className="text-[14px] font-normal ml-12 mt-2">{builder?.user?.bio}</div>
          <hr className="my-5" />
        </div>
      ))}
    </div>
  );
};

const PurpleBar = ({
  huddle,
  setOpen,
  open,
  optIn,
  role,
}: {
  huddle: Huddle;
  setOpen: (open) => void;
  open: boolean;
  optIn: any;
  role?: Role;
}) => {
  return (
    <div onClick={() => setOpen(!open)} className="flex items-center bg-[#F0ECFA] py-2 px-4 rounded-xl cursor-pointer">
      <div className="ml-10 hidden lg:flex">
        <TagCustom text="⚡Team Forming" hexColor={colorbg.yellow} />
      </div>
      {role !== Role.Customer && (
        <div className="text flex ml-11 hidden lg:flex">
          <div className="font-bold text-[#29174E] mr-2">Your Role</div>
          <div className="font-bold text-[#9281B3]">{optIn?.proposal_title}</div>
        </div>
      )}
      {huddle?.proposal?.selectedOptIns?.length > 0 && (
        <div className="team flex ml-12 hidden lg:flex">
          <div className="mr-2">
            <TeamAvatars huddle={huddle} />
          </div>
        </div>
      )}
      <div className="ml-auto font-medium text-[#3C3D3E] text-sm lg:hidden px-5">{'View Huddle Details'}</div>
      <div className="ml-auto mr-4 font-medium text-[#3C3D3E] text-2xl">{'>'}</div>
    </div>
  );
};

// Views based on user type & huddle status
const OpenViewTemplate = ({
  huddle,
  hexColor,
  updateText,
  summaryText,
  summaryIcon,
  tagText,
  showButtons = true,
}: {
  huddle: Huddle;
  hexColor: string;
  tagText?: string;
  updateText?: string;
  summaryText?: string;
  summaryIcon?: string;
  showButtons?: boolean;
}) => {
  return (
    <div className="font-medium">
      <div className="flex items-center">
        <div className="text-2xl">The team</div>
        <div className="ml-4">
          <TagCustom text={tagText} hexColor={hexColor} />
        </div>
        {showButtons && (
          <div className="ml-auto">
            <JoinRoomButton huddle={huddle} />
          </div>
        )}
      </div>
      {showButtons && (
        <>
          <div className="flex items-center mt-10">
            <div className={`px-2 py-1.5 rounded-lg ${hexColor} text-[12px]`}>📑</div>
            <div className="text-[17px] ml-4">{updateText}</div>
            <div className="ml-auto">
              <ViewHuddleAgreementButton huddle={huddle} />
            </div>
          </div>
          <hr className="my-5" />
        </>
      )}

      <div className="mt-10">
        <TeamList huddle={huddle} />
      </div>
      {summaryText && (
        <div className="flex items-center mb-8">
          <div className={`h-8 w-8 rounded-lg bg-[#FFF8E7] flex justify-center items-center`}>{summaryIcon}</div>
          <div className="ml-4 font-medium text-[#323232]">{summaryText}</div>
        </div>
      )}
    </div>
  );
};

const Active = ({ huddle, setOpen, open }: { huddle: Huddle; setOpen: (open) => void; open: boolean }) => {
  return open ? (
    <OpenViewTemplate
      huddle={huddle}
      hexColor={colorbg.green}
      tagText="Live Huddle"
      updateText="Everyone has agreed to the terms"
    />
  ) : (
    <div className="flex items-center gap-x-2">
      <JoinRoomButton huddle={huddle} />
      <FeedbackButton huddle={huddle} />
      <div className="ml-4">
        <ViewHuddleAgreementButton huddle={huddle} />
      </div>
      <div onClick={() => setOpen(!open)} className="text-[#3C3D3E] flex items-center ml-auto mr-4 cursor-pointer">
        View past details <div className="ml-2 font-medium text-[#3C3D3E] text-2xl">{'>'}</div>
      </div>
    </div>
  );
};

const TeamFormingDraft = ({
  huddle,
  setOpen,
  open,
  optIn,
  role,
}: {
  huddle: Huddle;
  setOpen: (open) => void;
  open: boolean;
  optIn: any;
  role: Role;
}) => {
  return open ? (
    <OpenViewTemplate
      huddle={huddle}
      tagText={'⚡ ️Team Forming'}
      hexColor={colorbg.yellow}
      updateText="Everyone has agreed to the terms"
      showButtons={false}
      summaryText="Actively bringing on more builders"
      summaryIcon="⚡"
    />
  ) : (
    <div className="flex">
      {role === Role.Customer && (
        <div className="mr-4">
          <JoinRoomButton huddle={huddle} />{' '}
        </div>
      )}
    </div>
  );
};

const ReviewAgreement = ({
  huddle,
  setOpen,
  open,
  optIn,
}: {
  huddle: Huddle;
  setOpen: (open) => void;
  open: boolean;
  optIn: any;
}) => {
  return open ? (
    <OpenViewTemplate
      huddle={huddle}
      tagText={'📑 Review Agreement'}
      hexColor={colorbg.gray}
      updateText="Please review the Huddle agreement"
    />
  ) : (
    <div className="flex">
      {huddle?.proposal?.selectedOptIns?.length > 0 && (
        <div className="team flex hidden lg:flex">
          <div className="mr-2">
            <TeamAvatars huddle={huddle} />
          </div>
        </div>
      )}
      <div className="">
        <JoinRoomButton huddle={huddle} />
      </div>
    </div>
  );
};

export const LeadReview = ({ huddle }: { huddle: Huddle }) => {
  return (
    <div className="flex flex-row gap-x-3">
      <div className="">
        <LeadReviewAgreementButton huddle={huddle} />
      </div>
      <div className="">
        <JoinRoomButton huddle={huddle} />
      </div>
    </div>
  );
};

const Past = ({ huddle, setOpen, open }: { huddle: Huddle; setOpen: (open) => void; open: boolean }) => {
  return open ? (
    <OpenViewTemplate
      huddle={huddle}
      hexColor={colorbg.gray}
      tagText="Past Huddle"
      updateText="Everyone has agreed to the terms"
      showButtons={false}
      summaryText="This huddle has been completed."
      summaryIcon="🎉"
    />
  ) : (
    <div className="flex items-center">
      {huddle?.proposal?.id && <ViewHuddleAgreementButton huddle={huddle} />}
      <div onClick={() => setOpen(!open)} className="text-[#3C3D3E] flex items-center ml-auto mr-4 cursor-pointer">
        View past details <div className="ml-2 font-medium text-[#3C3D3E] text-2xl">{'>'}</div>
      </div>
    </div>
  );
};

// Main Component
const Expansion = ({ huddle, open, setOpen }: { huddle: Huddle; open: boolean; setOpen: (open) => void }) => {
  const { user } = useAuth();
  const { Builder, Customer } = Role;
  const { TeamForming, LiveProject, PastProject } = HuddleStatus;
  const { Draft } = ProposalStatus;
  const optIn = huddle?.proposal?.selectedOptIns?.find(iterOptIn => iterOptIn.user.id === user.id);

  const teamLead = optIn?.proposal_teamLead;
  //
  return (
    <div>
      <div className={`transition-all ease-linear duration-700 ${open ? 'max-h-[1000px]' : 'max-h-20'}`}>
        {huddle?.status === LiveProject && <Active huddle={huddle} setOpen={setOpen} open={open} />}
        {huddle?.status === TeamForming && huddle.proposal.status === Draft && (
          <div className="">
            <TeamFormingDraft huddle={huddle} setOpen={setOpen} open={open} optIn={optIn} role={user?.role} />
          </div>
        )}
        {huddle?.status === TeamForming && huddle.proposal.status !== Draft && (
          <ReviewAgreement huddle={huddle} setOpen={setOpen} open={open} optIn={optIn} />
        )}
        {huddle?.status === PastProject && <Past huddle={huddle} setOpen={setOpen} open={open} />}
        {open && (
          <div
            className="flex items-center p-2 cursor-pointer justify-center rounded-xl mt-5 font-bold text-black"
            onClick={() => setOpen(!open)}
          >
            <div className="mr-2">Minimize</div>
            <div className="mr-2 text-2xl -rotate-90">{'>'}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Expansion;
